import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {useSelector, useDispatch} from 'react-redux';

import {Chip} from '@spaced-out/ui-design-system/lib/components/Chip';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {CRM_PERMISSIONS} from 'src/utils/crm-new/common';
import DefaultFieldsCard from 'src/components/crm/applicants/body/tabComponents/overview/default-fields-card';
import CandidateFieldsCard from 'src/components/crm/applicants/body/tabComponents/overview/candidate-fields-card';
import SkillsCard from 'src/components/crm/applicants/body/tabComponents/overview/skills-card/skills-card';
import {useParams, useHistory} from 'src/rerouter/index';

import {
  Card,
  CardHeader,
  CardTitle,
  CardActions,
  CardContent,
} from '@spaced-out/ui-design-system/lib/components/Card';

import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';

import {
  BodyMedium,
  BodySmall,
  BodyMediumBold,
  FormLabelSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {getApplicantInformation} from 'src/action-creators/crm';
import {
  selectApplicantInformation,
  selectHasCrmPermission,
  selectUserInfo,
} from 'src/selectors/crm';
import {useInflight} from 'src/hooks/redux';

import {
  Icon,
  SemanticIcon,
} from '@spaced-out/ui-design-system/lib/components/Icon';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';
import {getCandidateExperienceText} from 'src/utils/crm';
import SocialMediaProfile from 'src/components/crm/lib/social-media-profiles/social-media-profile';
import css from './candidate-details-card.css';
import classify from 'src/utils/classify';


type props = {
  candidateId: number;
};

const CandidateDetailsCard = ({candidateId}: props) => {
  const {t} = useLabelTranslation();
  const dispatch = useDispatch();
  const {location} = useHistory();
  const {candidateId: candidateIdParam} = location.query;
  const userInfo = useSelector(selectUserInfo);
  const viewCandidateDefaultField = selectHasCrmPermission(
    userInfo,
    CRM_PERMISSIONS.VIEW_CANDIDATE_DEFAULT_FIELD,
  );
  const viewCandidateCustomField = selectHasCrmPermission(
    userInfo,
    CRM_PERMISSIONS.VIEW_CANDIDATE_CUSTOM_FIELD,
  );

  const applicantDetails = useSelector(selectApplicantInformation);
  const [isLoading, setIsLoading] = React.useState(false);
  const isApplicantInformationLoading = useInflight(
    getApplicantInformation(candidateId),
  );
  const {resume} = applicantDetails ?? {};

  const {
    firstName,
    lastName,
    middleName,
    name: fullName,
    emails,
    phones,
    yearsOfExperience,
  } = resume ?? {};

  const initialCandidateDetails = {
    firstName,
    lastName,
    middleName,
    fullName,
    emails,
    phones,
    // @ts-expect-error - TS2345 - Argument of type 'number | undefined' is not assignable to parameter of type 'number'.
    years: Math.floor(yearsOfExperience),
    // @ts-expect-error - TS18048 - 'yearsOfExperience' is possibly 'undefined'.
    months: Math.floor(yearsOfExperience * 12) % 12,
  } as const;

  const fullNameFromParts =
    [firstName, middleName, lastName].filter(Boolean).join(' ') || '';

  const targetCandidateId = React.useMemo(() => {
    if (candidateIdParam) {
      const parsedId = Number(candidateIdParam);
      return isNaN(parsedId) ? candidateId : parsedId;
    }
    return candidateId;
  }, [candidateId, candidateIdParam]);

  React.useEffect(() => {
    const reFetch =
      !applicantDetails || applicantDetails.id !== targetCandidateId;

    if (reFetch && targetCandidateId) {
      setIsLoading(true);
      dispatch(getApplicantInformation(targetCandidateId)).finally(() =>
        setIsLoading(false),
      );
    } else {
      setIsLoading(false);
    }
  }, [dispatch, applicantDetails, targetCandidateId]);
  // Early return if no resume data
  if (!applicantDetails?.resume) {
    return (
      <Card>
        <CardContent>
          <BodyMedium>
            {t('No_resume_details_available', 'No resume details available')}
          </BodyMedium>
        </CardContent>
      </Card>
    );
  }

  if (isApplicantInformationLoading) {
    <CircularLoader className={css.loader} />;
  }

  return (
    <div className={css.candidateDetailsCardContainer}>
      <Card
        classNames={{
          wrapper: css.card,
        }}
      >
        <CardHeader>
          <CardTitle className={css.cardTitleContainer}>
            <SemanticIcon
              name="user"
              semantic="neutral"
              type="solid"
              size="small"
            />
            {t('Candidate_Details', 'Candidate Details')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className={css.candidateDetailsCardContentContainer}>
            <div>
              <BodySmall>{t('Full_Name', 'Full Name')}</BodySmall>
              <BodyMedium className={css.bold}>{fullNameFromParts}</BodyMedium>
            </div>
            <div>
              <BodySmall>{t('First_Name', 'First Name')}</BodySmall>
              <BodyMedium className={css.bold}>
                {applicantDetails.resume?.firstName || '-'}
              </BodyMedium>
            </div>
            <div>
              <BodySmall>{t('Middle_Name', 'Middle Name')}</BodySmall>
              <BodyMedium className={css.bold}>
                {applicantDetails.resume?.middleName || '-'}
              </BodyMedium>
            </div>
            <div>
              <BodySmall>{t('Last_Name', 'Last Name')}</BodySmall>
              <BodyMedium className={css.bold}>
                {applicantDetails.resume?.lastName || '-'}
              </BodyMedium>
            </div>
            <div className={css.candidateDetailBlock}>
              <BodySmall>{t('Gender', 'Gender')}</BodySmall>
              <BodyMedium className={css.bold}>
                {applicantDetails.resume?.gender || '-'}
              </BodyMedium>
            </div>
            <div className={css.candidateDetailBlock}>
              <BodySmall>{t('Email_ID', 'Email ID')}</BodySmall>
              <div className={css.candidateEmailsContainer}>
                {applicantDetails.resume?.emails?.map((email: any) => (
                  <Chip semantic="primary" size="medium" key={email}>
                    {email}
                  </Chip>
                ))}
              </div>
            </div>
            <div className={css.candidateDetailBlock}>
              <BodySmall>{t('Phone', 'Phone')}</BodySmall>
              <div className={css.candidateEmailsContainer}>
                {applicantDetails.resume?.phones?.map((email: any) => (
                  <Chip semantic="primary" size="medium" key={email}>
                    {email}
                  </Chip>
                ))}
              </div>
            </div>
            <div className={css.candidateDetailBlock}>
              <BodySmall>{t('Total_Experience', 'Total Experience')}</BodySmall>
              <BodyMedium className={css.bold}>
                {getCandidateExperienceText(
                  applicantDetails.resume?.yearsOfExperience ?? 0,
                )}
              </BodyMedium>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card
        classNames={{
          wrapper: css.card,
        }}
      >
        <CardHeader>
          <CardTitle className={css.cardTitleContainer}>
            <SemanticIcon
              name="briefcase"
              semantic="neutral"
              type="solid"
              size="small"
            />
            {t('Work_Experience', 'Work Experience')}
          </CardTitle>
        </CardHeader>
        <CardContent className={css.educationCardWrapper}>
          {/* @ts-expect-error - TS18048 - 'applicantDetails.resume.experiences.length' is possibly 'undefined'. */}
          {applicantDetails.resume?.experiences?.length > 0 ? (
            [...(applicantDetails.resume?.experiences || [])]
              .sort((a, b) => {
                if (a.isCurrent && !b.isCurrent) {
                  return -1;
                }
                if (!a.isCurrent && b.isCurrent) {
                  return 1;
                }

                const aEndYear = a.isCurrent
                  ? new Date().getFullYear()
                  : a.endYear || -1;
                const bEndYear = b.isCurrent
                  ? new Date().getFullYear()
                  : b.endYear || -1;

                return bEndYear - aEndYear;
              })
              .map((experience, index) => (
                <div
                  key={experience.experienceOrg}
                  // className={css.experienceContainer}
                  className={classify(css.experienceContainer, {
                    [css.noSeprator]:
                      // @ts-expect-error - TS18048 - 'applicantDetails.resume.experiences.length' is possibly 'undefined'.
                      applicantDetails.resume?.experiences?.length - 1 ===
                      index,
                  })}
                >
                  <BodyMediumBold>{experience.title}</BodyMediumBold>
                  <BodySmall>
                    <Icon name="building" color="secondary" />
                    {experience.experienceOrg || '-'}
                  </BodySmall>
                  <BodySmall>
                    <Icon name="location-dot" color="secondary" />
                    {experience.location || '-'}
                  </BodySmall>
                  <BodySmall>
                    <Icon name="clock" color="secondary" />
                    {formatDateRange(
                      experience.startYear,
                      experience.startMonth,
                      experience.endYear,
                      experience.endMonth,
                      experience.isCurrent,
                    )}{' '}
                    •{' '}
                    {calculateExperienceDuration(
                      experience.startYear,
                      experience.startMonth,
                      experience.endYear,
                      experience.endMonth,
                      experience.isCurrent,
                    )}
                  </BodySmall>
                </div>
              ))
          ) : (
            <CandidateFieldsEmptyState
              title={t('No_Work_Experience', 'No Work Experience')}
              description={t(
                'No_work_experience_details_available',
                'No work experience details available',
              )}
              image="data"
            />
          )}
        </CardContent>
      </Card>
      <Card
        classNames={{
          wrapper: css.card,
        }}
      >
        <CardHeader>
          <CardTitle className={css.cardTitleContainer}>
            <SemanticIcon
              name="user-graduate"
              semantic="neutral"
              type="solid"
              size="small"
            />
            {t('Education', 'Education')}
          </CardTitle>
        </CardHeader>
        <CardContent className={css.educationCardWrapper}>
          {/* @ts-expect-error - TS18048 - 'applicantDetails.resume.educations.length' is possibly 'undefined'. */}
          {applicantDetails.resume?.educations?.length > 0 ? (
            applicantDetails.resume?.educations?.map(
              (education: any, index: any) => (
                <div
                  key={education.educationOrg}
                  className={classify(css.educationContainer, {
                    [css.noSeprator]:
                      // @ts-expect-error - TS18048 - 'applicantDetails.resume.educations.length' is possibly 'undefined'.
                      applicantDetails.resume?.educations?.length - 1 === index,
                  })}
                >
                  <BodyMediumBold>{education.degree || ''}</BodyMediumBold>
                  <BodySmall>
                    <Icon name="building" color="secondary" />
                    {education.educationOrg || '-'}
                  </BodySmall>
                  <BodySmall>
                    <Icon name="location-dot" color="secondary" />
                    {education.location || '-'}
                  </BodySmall>
                  <BodySmall>
                    <Icon name="clock" color="secondary" />
                    {education.startYear > 0 ? education.startYear : '-'} -{' '}
                    {education.endYear > 0 ? education.endYear : '-'}
                  </BodySmall>
                </div>
              ),
            )
          ) : (
            <CandidateFieldsEmptyState
              title={t('No_Education_Details', 'No Education Details')}
              description={t(
                'No_educational_background_available',
                'No educational background available',
              )}
              image="data"
            />
          )}
        </CardContent>
      </Card>
      <SkillsCard />
      {/* @ts-expect-error - TS2322 - Type 'Candidate' is not assignable to type 'Application'. */}
      <SocialMediaProfile applicantDetails={applicantDetails} />
      {viewCandidateDefaultField && (
        <DefaultFieldsCard showEditButton={false} />
      )}
      {viewCandidateCustomField && (
        <CandidateFieldsCard showEditButton={false} />
      )}
    </div>
  );
};

export default CandidateDetailsCard;

export const formatDateRange = (
  startYear: number,
  startMonth: number,
  endYear: number,
  endMonth: number,

  isCurrent: boolean,
): string => {
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const formatDate = (year: number, month: number) => {
    if (!year || year <= 0) {
      return '';
    }

    if (month >= 1 && month <= 12) {
      return `${monthNames[month - 1]} ${year}`;
    }

    return `${year}`;
  };

  if (!startYear || startYear <= 0) {
    return '-';
  }

  const formattedStartDate = formatDate(startYear, startMonth);

  if (isCurrent || endYear <= 0) {
    return `${formattedStartDate} - Current`;
  }

  const formattedEndDate = formatDate(endYear, endMonth);

  if (formattedEndDate) {
    return `${formattedStartDate} - ${formattedEndDate}`;
  }

  return formattedStartDate;
};

export const calculateExperienceDuration = (
  startYear: number,
  startMonth: number,
  endYear: number,
  endMonth: number,

  isCurrent: boolean = false,
): string => {
  if (!startYear || startYear <= 0) {
    return '';
  }

  const now = new Date();
  const end =
    isCurrent || !endYear || endYear <= 0
      ? now
      : new Date(endYear, endMonth > 0 ? endMonth - 1 : 11, 1);

  const start = new Date(startYear, startMonth > 0 ? startMonth - 1 : 0, 1);

  let yearsDiff = end.getFullYear() - start.getFullYear();
  let monthsDiff = end.getMonth() - start.getMonth();

  if (monthsDiff < 0) {
    yearsDiff--;
    monthsDiff += 12;
  }
  const years = yearsDiff > 0 ? `${yearsDiff} yrs ` : '';
  const months = monthsDiff > 0 ? `${monthsDiff} month` : '';

  return `${years}${months}`.trim();
};

// @ts-expect-error - TS7031 - Binding element 'description' implicitly has an 'any' type. | TS7031 - Binding element 'title' implicitly has an 'any' type. | TS7031 - Binding element 'image' implicitly has an 'any' type.
const CandidateFieldsEmptyState = ({description, title, image}) => {
  return (
    <EmptyState description={description} title={title} imageVariant={image} />
  );
};
